import { createThemeButtonDataLegacy, createThemeButtonHoverLegacy } from '../common/schemas/legacy/theme-button';
import { createAnalyticsSettingsLegacy } from '../common/schemas/legacy/analytics';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';

import type { FormButtonSettingsData } from '../form/schemas/legacy/form-settings';

import { createPaymentElementLegacy } from './schemas/legacy/payment-element';

import type {
    LegacyPaymentFormDefault,
    LegacyPaymentFormWidget,
    LegacyPaymentFormZero,
    PaymentFormWidgetEntity,
} from './schemas';

/**
 * Host-only values the package deliberately doesn't own — i18n strings and the
 * default button-icon SVG template. The host model passes them in, mirroring
 * the form/button widgets' overrides convention.
 */
export type CreateLegacyPaymentFormWidgetOptions = {
    texts?: {
        /** `crm_deal_name` */
        dealName?: string;
        /** `pay` */
        buttonLabel?: string;
        /** `control_product_name` */
        productName?: string;
        /** `custom_payment_label` */
        customPaymentName?: string;
        /** `control_promocode_label_placeholder` */
        promoCodeLabel?: string;
        /** `control_promocode_placeholder_placeholder` */
        promoCodePlaceholder?: string;
    };
    /** Default icon SVG (`DEFAULT_BUTTON_ICON.icon` in the host). */
    buttonIcon?: string;
};

const createFormButtonSettings = (label: string, icon: string): FormButtonSettingsData => ({
    label,
    styleType: 'primary',
    size: 'M',
    buttonBase: createThemeButtonDataLegacy(),
    buttonHover: createThemeButtonHoverLegacy(),
    widthType: 'full',
    widthValue: 260,
    buttonIcon: {
        isActive: false,
        icon,
        position: 'L',
    },
    additionalEffects: {
        isActive: false,
        effectCondition: 'hover',
        hoverEffects: 'bounce',
        staticEffects: 'shine',
    },
    isIndependentCorners: false,
    advancedRadius: {
        borderBottomLeftRadius: null,
        borderBottomRightRadius: null,
        borderTopLeftRadius: null,
        borderTopRightRadius: null,
    },
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: null,
});

/**
 * Field values shared by both placement variants — everything except
 * `position` and `isSynchronizeStyle` (the only fields the variants diverge on).
 */
const createCommonData = (
    options?: CreateLegacyPaymentFormWidgetOptions
): Omit<LegacyPaymentFormDefault['data'], 'position'> => ({
    selectedAddressBook: null,
    selectedPipeline: null,
    selectedPipelineStep: null,
    selectedPipelineSettings: null,
    createAddressBook: false,
    dealName: options?.texts?.dealName ?? '',

    addCrmTags: false,
    contactTags: [],
    addAddressBookTags: false,
    contactAddressBookTags: [],

    elements: [
        {
            type: 'email',
            label: 'Email',
            placeholder: 'username@gmail.com',
            required: true,
            protected: true,
            crmContactVariable: null,
            crmDealVariable: null,
            variable: 'email',
            htmlInputType: 'email',
            isDynamicPlaceholderEnabled: false,
            autocompleteType: 'email',
        },
    ],
    inputSettings: {
        modalBackgroundColor: '#fbfcfc',
        modalBorderRadius: 6,
        modalPadding: 45,
        showLabels: true,
        inputBorderType: 'fullBorder',
        formStyleType: 'vertical',
        backgroundColor: '#fff',
        borderColor: '#ddd',
        textColor: '#333',
        inputTextColor: '#333',
        borderRadius: 3,
        inputSize: 'M',
        horizontalPosition: 'L',
    },
    formButtonSettings: createFormButtonSettings(options?.texts?.buttonLabel ?? '', options?.buttonIcon ?? ''),
    additionalFormSettings: {
        actionAfterConfirmType: 'link',
        openLink: null,
        promoCodeSettings: {
            isEnabled: false,
            label: options?.texts?.promoCodeLabel ?? '',
            placeholder: options?.texts?.promoCodePlaceholder ?? '',
            selectedPromoCode: null,
        },
    },
    analyticsSettings: createAnalyticsSettingsLegacy(),

    paymentFormName: 'Payment form',
    productType: 'default',
    productName: options?.texts?.productName ?? '',
    productSettings: null,
    paymentElements: [
        createPaymentElementLegacy({
            customPaymentName: options?.texts?.customPaymentName ?? '',
        }),
    ],

    formView: 'modal',
    isShowModal: false,
    modalTextHint: null,
    isSynchronizeStyle: true,

    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
});

const createDefaultData = (options?: CreateLegacyPaymentFormWidgetOptions): LegacyPaymentFormDefault['data'] => ({
    ...createCommonData(options),
    position: createWidgetPositionLegacy({
        x: 0,
        y: 0,
        zIndex: 1,
        rotate: 0,
        mobileX: 0,
        mobileY: 0,
        mobileZIndex: 1,
        mobileRotate: 0,
        width: 100,
    }),
});

const createZeroData = (options?: CreateLegacyPaymentFormWidgetOptions): LegacyPaymentFormZero['data'] => ({
    ...createCommonData(options),
    // Zero blocks are laid out per-device — start with sync off (form/button precedent).
    isSynchronizeStyle: false,
    // Height is a placeholder: the payment-form view DOM-syncs the rendered
    // content height into the grid (the form's `setupZeroHeightSync` pattern).
    position: createExtendedPositionLegacy({
        width: 400,
        tabletWidth: 400,
        mobileWidth: 320,
        height: 360,
        tabletHeight: 360,
        mobileHeight: 360,
    }),
});

export type LegacyPaymentFormWidgetVariant = 'default' | 'zero';

/**
 * Factory for the current legacy payment-form entity — matches the persisted
 * widget JSON shape (`{ id, name: 'payment', columnId, data, … }`) and the host
 * model's `paymentFormData()` defaults. Variant selects between default-column
 * placement (no `type` field, simple position grid) and zero-block placement
 * (`type: 'zero'`, extended-position grid).
 */
export function createLegacyPaymentFormWidget(
    variant?: 'default',
    options?: CreateLegacyPaymentFormWidgetOptions
): LegacyPaymentFormDefault;
export function createLegacyPaymentFormWidget(
    variant: 'zero',
    options?: CreateLegacyPaymentFormWidgetOptions
): LegacyPaymentFormZero;
export function createLegacyPaymentFormWidget(
    variant: LegacyPaymentFormWidgetVariant = 'default',
    options?: CreateLegacyPaymentFormWidgetOptions
): LegacyPaymentFormWidget {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'payment',
            columnId: '',
            type: 'zero',
            data: createZeroData(options),
        };
    }

    return {
        id: '',
        name: 'payment',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new payment-form widgets. While only `legacy` exists, this delegates to
 * {@link createLegacyPaymentFormWidget}; pass the variant to opt into the
 * zero-block tag.
 */
export function createPaymentFormWidget(
    variant?: 'default',
    options?: CreateLegacyPaymentFormWidgetOptions
): LegacyPaymentFormDefault;
export function createPaymentFormWidget(
    variant: 'zero',
    options?: CreateLegacyPaymentFormWidgetOptions
): LegacyPaymentFormZero;
export function createPaymentFormWidget(
    variant: LegacyPaymentFormWidgetVariant = 'default',
    options?: CreateLegacyPaymentFormWidgetOptions
): PaymentFormWidgetEntity {
    return variant === 'zero'
        ? createLegacyPaymentFormWidget('zero', options)
        : createLegacyPaymentFormWidget('default', options);
}
